<template>
  <PremiumMetricWidgetBase :widget="widget" v-slot="{ metric }" :state="state">
    <div class="chart-wrapper">
      <apexchart height="100%" width="100%" :options="chartOptions" :series="series(metric)" />
    </div>
  </PremiumMetricWidgetBase>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import dayjs from 'dayjs';
import PremiumMetricWidgetBase from './PremiumMetricWidgetBase.vue';
import { formatNumber } from '@/utils/formatNumbers';
import {
  getChartExportSettings,
  getDefaultChartOptions,
  type BaseChartOptions,
} from '@/utils/chartSettings';
import type { WidgetFilter, WidgetObject, WidgetSetting } from '@/types/Widget';

const props = defineProps<{
  widget: WidgetObject;
  area?: WidgetFilter;
  time?: WidgetFilter;
  zone?: WidgetSetting;
  cluster?: WidgetSetting;
  day?: WidgetSetting;
}>();

const defaultChartOptions = getDefaultChartOptions(props.widget.code_name);

const state = ref<string | null>(null);
const isDailyMode = computed(() => props.time?.name === 'Day');

const zoneNames = (metric: any) => {
  return Object.keys(metric.data.data['Site']['Total'] || {});
};

const dates = (metric: any) => {
  return Object.keys(metric.data.data['Site']['Day'] || {});
};

const series = (metric: any) => {
  try {
    if (isDailyMode.value) {
      return zoneNames(metric).map((zone: string) => ({
        name: zone,
        data: dates(metric).map((date: string) => ({
          x: date,
          y: metric.data.data['Site']['Day'][date][zone]['Engaged Percentage'],
        })),
      }));
    } else {
      return [
        {
          name: 'First Zone Visited',
          data: zoneNames(metric).map((zone: string) => ({
            x: zone,
            y: metric.data.data['Site']['Total'][zone]['Engaged Percentage'],
          })),
        },
      ];
    }
  } catch (error) {
    console.error(error);
    return [];
  }
};

const chartOptions = computed<BaseChartOptions>(() => {
  const baseOptions: BaseChartOptions = {
    chart: {
      ...defaultChartOptions.value.chart,
      type: 'bar',
      toolbar: getChartExportSettings(
        props.widget.name,
        props.area?.name || '',
        props.time?.name || '',
        props.zone?.title || '',
        props.cluster?.title || '',
        props.day?.title || '',
      ),
    },
    dataLabels: {
      enabled: false,
    },
    yaxis: {
      show: true,
      labels: {
        formatter: (val: number) => `${formatNumber(100 * val, 2)}%`,
      },
    },
    xaxis: {
      type: 'category',
      tooltip: {
        enabled: false,
      },
      labels: {
        formatter: (val: string) => {
          if (isDailyMode.value) {
            return dayjs(val).format('DD MMM');
          } else {
            return val;
          }
        },
      },
    },
  };
  return baseOptions;
});
</script>
